package de.oevbs.kunde.kundenportal2

import de.oevbs.kunde.kundenportal2.authentication.SessionTime
import de.oevbs.sfa.communication.ServiceMessage
import de.oevbs.sfa.communication.Severity
import dev.fritz2.core.Id

object PortalMessages {

    private val kupoService = ServiceInfo.name

    /**
     * Baut eine ServiceMessage.Validation ohne Pfadangabe von der übergebenen Quelle mit den folgenden Parametern:
     *
     * @param message Die Fehlermeldung des Quellsystems
     * @param id Optionale eindeutige Id. Ohne Angabe wird eine zufällige Id generiert.
     * @param severity Optionale Angabe, default = Severity.Error
     * @param source Optional das Quellsystem der Fehlermeldung. Wird ohne Angabe auf [kupoService] gesetzt.
     *              Wenn Nachrichten aus anderen Services angezeigt werden, sollte der Servicename und eine Bezeichnung
     *              für den Endpunkt genutzt werden, bspw. "bp-crm-service2/updateParter".
     * */
    fun portalMessage(
        message: String,
        id: String = Id.next(),
        severity: Severity = Severity.Error,
        source: String = kupoService
    ) = ServiceMessage.Validation(
        message = if (source != kupoService) "Das System meldet: '${message}'" else message,
        id = id,
        severity = severity,
        source = source,
        path = "",
    )

    // ------- Info / Success
    val tokenRefreshInfo = portalMessage(
        id = "tokenRefreshInfo",
        severity = Severity.Info,
        message = "Die Aktion war erfolgeich, aber Ihre Benutzerrechte konnten nicht aktualisiert werden. Bitte loggen Sie sich erneut ein."
    )
    val agentUsingDefaultInfo = portalMessage(
        id = "agentUsingDefaultInfo",
        severity = Severity.Info,
        message = "Es wurde keine Beraterinformationen zu Ihren Partnerdaten gefunden. Stattdessen wird der Kundenservice angezeigt."
    )
    val pinLetterNewInfo = portalMessage(
        id = "pinLetterNewInfo",
        severity = Severity.Info,
        message = "Es wurde ein neuer Freischaltcode angefordert, den Sie in den nächsten Tagen per Post erhalten."
    )
    val sessionTimedOut = portalMessage(
        id = "sessionTimedOut",
        severity = Severity.Info,
        message = "Sie wurden nach ${SessionTime.MAX_DURATIONS_SECONDS / 60} Minuten Inaktivität ausgeloggt."
    )
    val accountDeleted = portalMessage(
        id = "accountDeleted",
        severity = Severity.Success,
        message = "Ihr Account für 'Meine Öffentliche' wurde erfolgreich gelöscht."
    )

    // ------- Warning
    val consentCheckFailed = portalMessage(
        id = "consentCheckFailed",
        severity = Severity.Warning,
        message = "Fehler beim Prüfen Ihrer Einwilligungserklärungen. Bitte bearbeiten Sie Ihre Einwilligungen im Bereich Meine Daten, falls Sie dies noch nicht getan haben."
    )
    val messageCouldNotMarkReadWarning = portalMessage(
        id = "messageCouldNotMarkRead",
        severity = Severity.Warning,
        message = "Ihre Nachricht konnte nicht als gelesen markiert werden."
    )
    val contactCouldNotCreateWarning = portalMessage(
        id = "contactCouldNotCreateWarning",
        severity = Severity.Warning,
        message = "Es konnte kein Kontakt in Ihrer Kundenakte erstellt werden."
    )
    val contactOneOrMoreCouldNotCreateWarning = portalMessage(
        id = "contactOneOrMoreCouldNotCreateWarning",
        severity = Severity.Warning,
        message = "Warnung: Für mindestens einen Vertrag konnte keine Notiz in Ihrer Kundenakte erstellt werden."
    )
    val activityCouldNotCreateWarning = portalMessage(
        id = "activityCouldNotCreateWarning",
        severity = Severity.Warning,
        message = "Es konnte keine Aktivität in Ihrer Kundenakte erstellt werden."
    )
    val customerMailCouldNotSendWarning = portalMessage(
        id = "customerMailCouldNotSendWarning",
        severity = Severity.Warning,
        message = "Der Prozess wurde erfolgreich abgeschlossen - es konnte aber keine Mail-Bestätigung an Sie versendet werden."
    )
    val advisorMailCouldNotSendWarning = portalMessage(
        id = "advisorMailCouldNotSendWarning",
        severity = Severity.Warning,
        message = "Es konnte keine Mail an Ihre:n Berater:in versendet werden."
    )
    val pinSuccessButContactFailedWarning = portalMessage(
        id = "pinSuccessButContactFailedWarning",
        severity = Severity.Warning,
        message = "Die Freischaltcode-Aktion war erfolgreich, aber es trat ein Fehler beim Anlegen des Kundenkontaktes in Ihren Vertragsdaten auf."
    )

    // ------- Error, Fatal, Exceptions
    val consentCouldNotUpdate = portalMessage(
        id = "consentCouldNotUpdate",
        message = "Ihre Einwilligungserklärung konnte nicht aktualisiert werden. Bitte wenden Sie sich an den Kundenservice."
    )
    val consentLoadingFailed = portalMessage(
        id = "consentLoadingFailed",
        message = "Ihre Einwilligungserklärungen konnten nicht geladen werden. Bitte wenden Sie sich an den Kundenservice."
    )
    val securityError = portalMessage(
        id = "securityError",
        message = "Ihre Anfrage wurde aufgrund einer Sicherheitsregel abgelehnt. Bitte wenden Sie sich an den Kundenservice."
    )
    val connectionError = portalMessage(
        id = "connectionError",
        message = "Zeitüberschreitung: Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut."
    )
    val unknownError = portalMessage(
        id = "unknownError",
        message = "Es ist ein unbekannter Fehler aufgetreten."
    )
    val notFound = portalMessage(
        id = "notFound",
        message = "Fehler - Service oder Datei wurde nicht gefunden."
    )
    val technicalError = portalMessage(
        id = "technicalError",
        message = "Es ist ein technischer Fehler aufgetreten."
    )
    val noSuccess = portalMessage(
        id = "noSuccess",
        message = "Die Anfrage konnte nicht bearbeitet werden. Bitte versuchen Sie es erneut oder wenden Sie sich an den Kundenservice."
    )
    val tokenMailCouldNotSend = portalMessage(
        id = "tokenMailCouldNotSend",
        message = "Es konnte keine Verifizierungsmail an Sie versendet werden. Bitte starten Sie die Registrierung erneut oder wenden Sie sich an den Kundenservice."
    )
    val notAValidEndpoint = portalMessage(
        id = "notAValidEndpoint",
        message = "Es wurde ein ungültiger Endpunkt aufgerufen. Bitte informieren Sie den Kundenservice."
    )
    val registrationCouldNotSave = portalMessage(
        id = "registrationCouldNotSave",
        message = "Ihre Registrierung konnte nicht gespeichert werden. Bitte starten Sie die Registrierung später erneut."
    )
    val registrationCouldNotFind = portalMessage(
        id = "registrationCouldNotFind",
        message = "Der verwendete Link ist nicht mehr gültig. Bitte starten Sie die Registrierung erneut."
    )
    val contractNumberCouldNotFind = portalMessage(
        id = "noContractNumberFound",
        message = "Es wurde keine Vertragsnummer gefunden."
    )
    val partnerIdCouldNotFind = portalMessage(
        id = "partnerIdCouldNotFind",
        message = "Es wurde keine Partnernummer gefunden, bitte melden Sie sich erneut an. Wenn das Problem weiterhin besteht, wenden Sie sich bitte an den Kundenservice."
    )
    val messageIdCouldNotFind = portalMessage(
        id = "messageIdCouldNotFind",
        message = "Es wurde keine Nachrichten-Nummer gefunden."
    )
    val loginNotAuthorized = portalMessage(
        id = "loginUnauthorized",
        message = "Sie konnten nicht angemeldet werden. Bitte überprüfen Sie die Eingaben - sind Sie mit diesen Daten für das Kundenportal registriert? Nach 5 Fehlversuchen wird Ihr Account gesperrt. Kontaktieren Sie in diesem Fall bitte den Kundenservice."
    )
    val loginCouldNotLogin = portalMessage(
        id = "loginCouldNotLogin",
        message = "Unerwarteter Fehler beim Login."
    )
    val modelInvalid = portalMessage(
        id = "modelInvalid",
        message = "Die empfangenen Daten sind ungültig. Bitte starten Sie den Prozess erneut."
    )
    val partnerCouldNotVerify = portalMessage( // 409
        id = "partnerCouldNotVerify",
        message = "Ihre Vertragsdaten konnten nicht bestätigt werden. Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut oder wenden Sie sich an den Kundenservice."
    )
    val workitemCouldNotCreate = portalMessage(
        id = "couldNotCreateWorkitem",
        message = "Mindestens ein Auftrag konnte nicht an eine:n Bearbeiter:in übermittelt werden. Bitte wenden Sie sich an den Kundenservice."
    )
    val passCouldNotChange = portalMessage(
        id = "passCouldNotChange",
        message = "Ihr Passwort konnte nicht geändert werden. Bitte versuchen Sie es später nochmal."
    )
    val accountCouldNotUnregister = portalMessage(
        id = "accountCouldNotUnregister",
        message = "Ihr Account konnte nicht von Ihren Partnerdaten getrennt werden. Bitte wenden Sie sich an den Kundenservice."
    )
    val accountCouldNotDelete = portalMessage(
        id = "accountCouldNotDelete",
        message = "Ihr Account konnte nicht gelöscht werden. Bitte wenden Sie sich an den Kundenservice."
    )
    val accountCouldNotDeleteButDeregistered = portalMessage(
        id = "accountCouldNotDeleteButDeregistered",
        message = "Ihr Account konnte nicht gelöscht werden, wurde aber von Ihren Partnerdaten getrennt. Bitte wenden Sie sich an den Kundenservice."
    )
    val partnerAlreadyInUse = portalMessage( // 422
        id = "partnerAlreadyInUse",
        message = "Diese Vertragsdaten werden bereits mit einem anderen Account verwendet. Nutzen Sie die Zugangsdaten-Vergessen Funktion beim Login, um den Benutzernamen zu erfahren, oder wenden Sie sich an den Kundenservice."
    )
    val mailCouldNotSave = portalMessage(
        id = "mailCouldNotSave",
        message = "Ihre Mail-Adresse konnte nicht gespeichert werden. Bitte versuchen Sie es erneut oder wenden Sie sich an den Kundenservice."
    )
    val partnerNotIdentified = portalMessage( // 400
        id = "partnerNotFound",
        message = "Sie konnten nicht identifiziert werden. Bitte prüfen Sie Ihre Eingabe."
    )
    val accountDoesNotExist = portalMessage(
        id = "accountDoesNotExist",
        message = "Ihr Benutzername konnte nicht in der Datenbank gefunden werden."
    )
    val accountCouldNotUpdate = portalMessage(
        id = "accountCouldNotUpdate",
        message = "Ihre Benutzerdaten konnten nicht aktualisiert werden."
    )
    val partnerCouldNotUpdateGeneral = portalMessage(
        id = "partnerCouldNotUpdateGeneral",
        message = "Ihre Partnerdaten konnten nicht aktualisiert werden."
    )
    val partnerCouldNotUpdateDataError = portalMessage(
        id = "partnerCouldNotUpdateDataError",
        message = "Ihre Partnerdaten konnten nicht aktualisiert werden, weil ein Datenfehler aufgetreten ist."
    )
    val partnerCouldNotSetMail = portalMessage(
        id = "partnerCouldNotSetMail",
        message = "Ihre Mailadresse konnte nicht aktualisiert werden."
    )
    val addressCouldNotCheck = portalMessage(
        id = "addressCouldNotCheck",
        message = "Ihre Adresseingabe konnte nicht geprüft werden."
    )
    val tokenInvalid = portalMessage(
        id = "tokenInvalid",
        message = "Die von Ihnen aufgerufene Seite ist nicht mehr gültig. Bitte starten Sie den Prozess erneut."
    )
    val passIsWrong = portalMessage(
        id = "passIsWrong",
        message = "Das aktuelle Passwort ist falsch."
    )
    val digiCardCouldNotRequest = portalMessage(
        id = "digiCardCouldNotRequest",
        message = "Die digitale Servicekarte konnte nicht beantragt werden. Wenn Sie heute bereits eine Karte für diesen Vertrag angefordert haben, versuchen Sie es morgen erneut. Andernfalls wenden Sie sich bitte an den Kundenservice."
    )
    val ivkCouldNotRequest = portalMessage(
        id = "ivkCouldNotRequest",
        message = "Die internationale Versicherungskarte konnte nicht beantragt werden. Wenn Sie heute bereits eine Karte für diesen Vertrag angefordert haben, versuchen Sie es morgen erneut. Andernfalls wenden Sie sich bitte an den Kundenservice."
    )
    val taxLimitReachedOrError = portalMessage(
        id = "taxLimitReachedOrError",
        message = "Die Bescheinigungen konnten nicht angefordert werden. Es trat entweder ein technischer Fehler auf oder Sie haben heute bereits Bescheinigungen für diese Verträge angefordert."
    )
    val accountAlreadyExists = portalMessage( // 409
        id = "accountAlreadyExists",
        message = "Dieser Benutzername ist bereits vergeben. Bitte wählen Sie einen anderen Namen aus."
    )
    val accountCouldNotAuthenticate = portalMessage(
        id = "accountCouldNotAuthenticate",
        message = "Ihre Verträge konnten nicht verknüpft werden. Falls das Problem nach erneuter Anmeldung weiter besteht, wenden Sie sich bitte an den Kundenservice."
    )
    val verifyMailLimitReached = portalMessage( //Registration: Mail-Code
        id = "verifyMailLimitReached",
        message = "Sie haben heute bereits drei Verifizierungsmails angefordert - versuchen Sie es morgen wieder."
    )
    val partnerLoadingFailed = portalMessage(
        id = "partnerLoadingFailed",
        message = "Fehler beim Laden Ihrer Partnerdaten. Bitte wenden Sie sich an den Kundenservice."
    )
    val partnerBankDataLoadingFailed = portalMessage(
        id = "partnerBankDataLoadingFailed",
        message = "Fehler beim Laden Ihrer Bankverbindungen. Bitte wenden Sie sich an den Kundenservice."
    )
    val contractsLoadingFailed = portalMessage(
        id = "contractsLoadingFailed",
        message = "Fehler beim Laden Ihrer Vertragsdaten. Bitte wenden Sie sich an den Kundenservice."
    )
    val claimsLoadingFailed = portalMessage(
        id = "claimsLoadingFailed",
        message = "Fehler beim Laden Ihrer Schadenliste. Bitte wenden Sie sich an den Kundenservice."
    )
    val agentLoadingFailed = portalMessage(
        id = "agentLoadingFailed",
        message = "Fehler beim Laden der Daten Ihres Beraters. Bitte wenden Sie sich an den Kundenservice."
    )
    val inboxLoadingFailed = portalMessage(
        id = "inboxLoadingFailed",
        message = "Fehler beim Laden Ihres Posteingangs. Bitte wenden Sie sich an den Kundenservice."
    )
    val messageDetailsLoadingFailed = portalMessage(
        id = "messageDetailsLoadingFailed",
        message = "Fehler beim Laden einer Kontakt-Nachricht im Postfach. Bitte wenden Sie sich an den Kundenservice."
    )
    val contractDetailsLoadingFailed = portalMessage(
        id = "contractDetailsLoadingFailed",
        message = "Fehler beim Laden Ihrer Vertragsdetails. Bitte wenden Sie sich an den Kundenservice."
    )
    val pinLetterFailed = portalMessage(
        id = "pinLetterFailed",
        message = "Fehler beim Versenden Ihres Freischaltcodes. Bitte wenden Sie sich an den Kundenservice, um einen Brief zu erhalten."
    )
    val pinAlreadyPinAuthed = portalMessage( //Authentication: Brief-Code
        id = "pinAlreadyPinAuthed",
        message = "Ihre Vertragsdaten wurden bereits mit einem Freischaltcode bestätigt."
    )
    val pinWrongOrExpired = portalMessage( //Authentication: Brief-Code
        id = "pinWrongOrExpired",
        message = "Sie haben einen inkorrekten oder abgelaufenen Freischaltcode eingegeben. Bitte verwenden Sie den Code aus dem zuletzt erhaltenen Brief."
    )
    val pinLetterLimitReached = portalMessage(
        id = "pinLetterLimitReached",
        message = "Sie können nur einen Freischaltcode pro Tag anfordern. Sollten Sie insgesamt bereits 3 Briefe angefordert haben, kontaktieren Sie bitte den Kundenservice. Andernfalls versuchen Sie es morgen wieder."
    )
    val serviceInfoNotFound = portalMessage(
        id = "serviceInfoNotFound",
        message = "Fehler - Die Service-Informationen konnten nicht gefunden werden."
    )
    val serviceLimitReached = portalMessage(
        id = "serviceLimitReached",
        message = "Sie haben die maximale Anzahl an Anfragen für diesen Service erreicht. Versuchen Sie es morgen wieder."
    )
    val contactMessageCouldNotSend = portalMessage(
        id = "contactMessageCouldNotSend",
        message = "Beim Versenden Ihrer Mitteilung trat ein Fehler auf. Bitte benutzen Sie das Kontaktformular auf www.oeffentliche.de."
    )
    val contractNoSelection = portalMessage(
        id = "contractNoSelection",
        message = "Bitte wählen Sie einen Vertrag für diese Aktion aus."
    )

    // Formatvorlagen für Fehlermeldungen des Verwaltungstools
    // Parameter PartnerId
    const val TOOL_BP_ACC_NOTFOUND = "Zum Partner %s konnte kein Account gefunden werden."
    const val TOOL_BP_NOTLOADED = "Der Partner %s konnte nicht geladen werden."
    const val TOOL_BP_MAILCHANGE_FAIL =
        "Die Mailadresse des Partners %s konnte nicht geändert werden. Achtung - möglicher Datenschiefstand zur Account-Mail!"

    // Parameter Username
    const val TOOL_ACC_NOTFOUND = "Der Account %s konnte nicht gefunden werden."
    const val TOOL_ACC_DELETE_FAIL = "Der Account %s konnte nicht gelöscht werden."
    const val TOOL_ACC_UNLOCK_FAIL = "Der Account %s konnte nicht entsperrt werden."
    const val TOOL_ACC_CONNECTED_BP_NOTLOADED = "Der Partner von Account %s konnte nicht geladen werden."
    const val TOOL_ACC_CHANGEMAIL_FAILED = "Die neue Mailadresse konnte nicht im Account %s gespeichert werden."
    const val TOOO_ACC_AUTHED_BUT_NO_BP = "Der Account %s ist verknüpft, hat aber keine gültigen Partnerdaten."
    const val TOOL_ACC_PINLETTER_DENIED_NO_BP =
        "Der Account %s ist nicht crm-verknüpft und darf keinen PIN-Brief erhalten."
    const val TOOL_ACC_PINLETTER_DENIED_AUTHED =
        "Der Account %s ist schon pin-authentifiziert und darf keinen PIN-Brief erhalten."
    const val TOOL_ACC_PINLETTER_LIMIT_REACHED =
        "Der Account %s hat die maximale Anzahl PIN-Requests erreicht. Dieser Fehler sollte in der Kundenportal-Verwaltung nie auftreten - bitte an einen Techniker wenden."
    const val TOOL_ACC_PINLETTER_NOTSENT = "Der Freischaltcode-Brief für Account %s konnte nicht versendet werden."
    const val TOOL_PINLETTER_SENT_BUT_NO_CONTACT_WARNING =
        "Der Freischaltcode-Brief für Account %s wurde versendet, es konnte aber kein Kontakt angelegt werden."

    // Parameter Username und PartnerId
    const val TOOL_ACC_BP_DEREGISTER_FAIL = "Der Account %s konnte nicht vom Partner %s getrennt werden."
    const val TOOL_ACC_BP_DEREGISTERED_BUT_DELETE_FAIL =
        "Der Account %s konnte nicht gelöscht werden, wurde aber vom Partner %s getrennt."
    const val TOOL_ACC_BP_NOTFOUND_IN_DB = "Der Account %s von Partner %s konnte nicht gefunden werden."

    // 2 Parameter
    const val TOOL_MAIL_X_NOT_SENT_TO_Y = "Die Mail %s konnte nicht an %s gesendet werden."
}